import React, { useState, useEffect } from "react";
import { sanitizeHtml } from "@/lib/htmlSanitizer";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Check, X, ChevronRight, ChevronLeft, Info } from "lucide-react";
import { highlightDifferences, type Change } from "@/lib/diffHighlighter";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";

interface RevisionComparisonModalProps {
  isOpen: boolean;
  onClose: () => void;
  originalContent: string;
  revisedContent: string;
  onApply: () => void;
}

export const RevisionComparisonModal = ({
  isOpen,
  onClose,
  originalContent,
  revisedContent,
  onApply,
}: RevisionComparisonModalProps) => {
  const [diffData, setDiffData] = useState<{ original: string; revised: string; changes: Change[] }>({
    original: '',
    revised: '',
    changes: [],
  });
  const [acceptedChanges, setAcceptedChanges] = useState<Set<string>>(new Set());
  const [rejectedChanges, setRejectedChanges] = useState<Set<string>>(new Set());
  const [currentChangeIndex, setCurrentChangeIndex] = useState(0);
  const [selectedChangeId, setSelectedChangeId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && originalContent && revisedContent) {
      const diff = highlightDifferences(originalContent, revisedContent);
      setDiffData(diff);
      setAcceptedChanges(new Set());
      setRejectedChanges(new Set());
      setCurrentChangeIndex(0);
      setSelectedChangeId(null);
    }
  }, [isOpen, originalContent, revisedContent]);

  const currentChange = diffData.changes[currentChangeIndex];

  const handleAcceptChange = (changeId: string) => {
    setAcceptedChanges(prev => new Set([...prev, changeId]));
    setRejectedChanges(prev => {
      const next = new Set(prev);
      next.delete(changeId);
      return next;
    });
  };

  const handleRejectChange = (changeId: string) => {
    setRejectedChanges(prev => new Set([...prev, changeId]));
    setAcceptedChanges(prev => {
      const next = new Set(prev);
      next.delete(changeId);
      return next;
    });
  };

  const handleAcceptAll = () => {
    setAcceptedChanges(new Set(diffData.changes.map(c => c.id)));
    setRejectedChanges(new Set());
  };

  const handleRejectAll = () => {
    setRejectedChanges(new Set(diffData.changes.map(c => c.id)));
    setAcceptedChanges(new Set());
  };

  const handleNextChange = () => {
    if (currentChangeIndex < diffData.changes.length - 1) {
      setCurrentChangeIndex(currentChangeIndex + 1);
      setSelectedChangeId(diffData.changes[currentChangeIndex + 1].id);
    }
  };

  const handlePrevChange = () => {
    if (currentChangeIndex > 0) {
      setCurrentChangeIndex(currentChangeIndex - 1);
      setSelectedChangeId(diffData.changes[currentChangeIndex - 1].id);
    }
  };

  const getChangeTypeColor = (type: Change['type']) => {
    switch (type) {
      case 'addition': return 'bg-green-500/10 text-green-700 dark:text-green-400 border-green-500/50';
      case 'deletion': return 'bg-red-500/10 text-red-700 dark:text-red-400 border-red-500/50';
      case 'modification': return 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-blue-500/50';
    }
  };

  const acceptedCount = acceptedChanges.size;
  const totalChanges = diffData.changes.length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-7xl max-h-[95vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>Content Revision Comparison</DialogTitle>
          <DialogDescription>
            Review {totalChanges} changes made by AI. Green = additions, Red = deletions, Blue = modifications.
          </DialogDescription>
        </DialogHeader>

        {/* Change Navigator */}
        {currentChange && (
          <div className="border rounded-lg p-4 bg-muted/30 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Badge className={getChangeTypeColor(currentChange.type)}>
                  {currentChange.type}
                </Badge>
                <span className="text-sm text-muted-foreground">
                  Change {currentChangeIndex + 1} of {totalChanges}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handlePrevChange}
                  disabled={currentChangeIndex === 0}
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleNextChange}
                  disabled={currentChangeIndex === diffData.changes.length - 1}
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>

            {currentChange.explanation && (
              <div className="flex gap-2 text-sm bg-blue-500/5 border border-blue-500/20 rounded p-2">
                <Info className="h-4 w-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <span className="text-muted-foreground">{currentChange.explanation}</span>
              </div>
            )}

            <div className="flex gap-2">
              <Button
                size="sm"
                onClick={() => handleAcceptChange(currentChange.id)}
                variant={acceptedChanges.has(currentChange.id) ? "default" : "outline"}
                className="flex-1"
              >
                <Check className="h-4 w-4 mr-2" />
                {acceptedChanges.has(currentChange.id) ? 'Accepted' : 'Accept'}
              </Button>
              <Button
                size="sm"
                onClick={() => handleRejectChange(currentChange.id)}
                variant={rejectedChanges.has(currentChange.id) ? "destructive" : "outline"}
                className="flex-1"
              >
                <X className="h-4 w-4 mr-2" />
                {rejectedChanges.has(currentChange.id) ? 'Rejected' : 'Reject'}
              </Button>
            </div>
          </div>
        )}
        
        {/* Split View */}
        <div className="grid grid-cols-2 gap-4 flex-1 overflow-hidden min-h-0">
          <div className="flex flex-col border rounded-lg overflow-hidden">
            <div className="bg-muted px-4 py-2 font-semibold text-sm border-b flex items-center justify-between">
              <span>Original Content</span>
              <Badge variant="outline" className="text-xs">Before</Badge>
            </div>
            <ScrollArea className="flex-1 p-4">
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(diffData.original) }}
              />
            </ScrollArea>
          </div>
          
          <div className="flex flex-col border rounded-lg overflow-hidden">
            <div className="bg-muted px-4 py-2 font-semibold text-sm border-b flex items-center justify-between">
              <span>Revised Content</span>
              <Badge variant="outline" className="text-xs">After</Badge>
            </div>
            <ScrollArea className="flex-1 p-4">
              <div 
                className="prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: sanitizeHtml(diffData.revised) }}
              />
            </ScrollArea>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t">
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={handleAcceptAll}>
              Accept All ({totalChanges})
            </Button>
            <Button variant="outline" size="sm" onClick={handleRejectAll}>
              Reject All
            </Button>
          </div>
          
          <div className="flex gap-2">
            <span className="text-sm text-muted-foreground flex items-center">
              {acceptedCount} of {totalChanges} changes accepted
            </span>
            <Button variant="outline" onClick={onClose}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
            <Button onClick={onApply} disabled={acceptedCount === 0}>
              <Check className="h-4 w-4 mr-2" />
              Apply {acceptedCount} Change{acceptedCount !== 1 ? 's' : ''}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

